// Sprawdzanie treści wiadomości przed wysłaniem: długość, linki (tylko samochody.pl), niedozwolone słowa.
// Numery telefonów i adresy e-mail są dozwolone.
// CommonJS — moduł importuje też rest-api (lib/offer/subtitle.js).

const MAX_LENGTH = 2000

const TLD = 'pl|com|net|eu|org|io|info|me|biz|shop|online|site|store|xyz|top|ru|de|uk|ua|by|cz|sk|lt|lv|fr|it|es|nl|be|at|ch|us|co|ly|link|click|app|page|club|live|pro|tv|cc|ws|to|gl|gg|ai|dev|cloud|website|space|fun|vip|win|bid|loan|work'
const URL_RE = new RegExp(`(^|[^@\\w.-])((?:https?:\\/\\/)?(?:www\\.)?((?:[a-z0-9ąćęłńóśźż](?:[a-z0-9ąćęłńóśźż-]*[a-z0-9ąćęłńóśźż])?\\.)+(?:${TLD})))(?![a-z0-9ąćęłńóśźż-])(?::\\d+)?(?:[/?#][^\\s]*)?`, 'gi')
const SCHEME_RE = /\b(?:https?:\/\/|www\.)([^\s/?#:]+)/gi
const OWN = /(^|\.)samochody\.pl$/i

// Pierwsza domena w tekście ('' — brak), z pominięciem domen pasujących do `allowed`. Adresy e-mail (część po „@”)
// nie są linkami.
function findLink (text, allowed = null) {
    const value = String(text || '')
    for (const re of [SCHEME_RE, URL_RE]) {
        re.lastIndex = 0
        let m
        while ((m = re.exec(value))) {
            const host = (re === SCHEME_RE ? m[1] : m[3]).toLowerCase().replace(/\.$/, '')
            if (re === URL_RE && value[m.index + m[1].length - 1] === '@') continue
            if (!host.includes('.')) continue
            if (!allowed || !allowed.test(host)) return host
        }
    }
    return ''
}

// Pierwsza domena spoza samochody.pl w tekście ('' — brak).
const findForeignLink = (text) => findLink(text, OWN)

// Domyślna lista (gdy w panelu nie zapisano własnej). Wpis „rdzeń*” — słowo zaczynające się od rdzenia (odmiany);
// wpis bez „*” — tylko całe słowo. Przedrostki (np. „wy”, „roz”, „s”) dopasowywane automatycznie.
const DEFAULT_WORDS = [
    'kurw*', 'chuj*', 'huj*', 'pierdol*', 'pierdal*', 'jeban*', 'jebie*', 'jebać', 'jebac', 'jebn*', 'jebal*', 'jebał*',
    'pizd*', 'kutas*', 'fiut*', 'dziwk*', 'cipa', 'cipy', 'cipie', 'cipę', 'cipą', 'cipo', 'skurwi*', 'skurwy*',
]

const PREFIXES = ['', 'do', 'na', 'nad', 'o', 'ob', 'od', 'po', 'pod', 'prze', 'przy', 'roz', 's', 'u', 'w', 'wy', 'z', 'za', 'ze', 'wpie']
const LEET = { 0: 'o', 1: 'i', 3: 'e', 4: 'a', 5: 's', 7: 't', '@': 'a', $: 's' }
const LETTER = 'a-ząćęłńóśźż'

const collapse = (s) => s.replace(/(.)\1+/g, '$1')

// Słowa do sprawdzenia: małe litery, cyfry i znaki zamiast liter, powtórzone litery, litery rozdzielone kropkami/spacjami.
function wordsOf (text) {
    let value = String(text || '').toLowerCase()
    // „k u r w a”, „k.u.r.w.a” — pojedyncze znaki rozdzielone separatorami łączone w jedno słowo
    // (bez lookbehind: Safari < 16.4)
    value = value.replace(new RegExp(`(^|\\s)((?:[${LETTER}0-9@$][\\s._*-]+){2,}[${LETTER}0-9@$])(?=\\s|$|[,.!?])`, 'g'), (m, start, word) => start + word.replace(/[\s._*-]+/g, ''))
    return value
        .split(new RegExp(`[^${LETTER}0-9@$]+`))
        .filter(Boolean)
        .map(w => collapse(w.replace(/[0-9@$]/g, c => LEET[c] || c)))
        .filter(w => new RegExp(`[${LETTER}]`).test(w))
}

function compile (entries) {
    return (entries || []).map(e => String(e || '').trim().toLowerCase()).filter(Boolean).map(e => {
        const prefix = e.endsWith('*')
        return { stem: collapse(prefix ? e.slice(0, -1) : e), prefix, label: e }
    }).filter(e => e.stem.length >= 3)
}

// Pierwsze niedozwolone słowo (w brzmieniu z wiadomości, po normalizacji) albo ''.
function findBadWord (text, entries = DEFAULT_WORDS) {
    const rules = compile(entries)
    for (const word of wordsOf(text)) {
        for (const p of PREFIXES) {
            if (p && !word.startsWith(p)) continue
            const rest = word.slice(p.length)
            for (const r of rules) {
                if (r.prefix ? rest.startsWith(r.stem) : rest === r.stem) return word
            }
        }
    }
    return ''
}

// „kurwa” → „k***a”
const maskWord = (w) => w.length <= 2 ? '*'.repeat(w.length) : w[0] + '*'.repeat(w.length - 2) + w[w.length - 1]

// Treść do porównań (ta sama wiadomość do wielu osób): małe litery, bez znaków interpunkcyjnych i nadmiarowych spacji.
const normalizeText = (text) => String(text || '').toLowerCase().replace(/[^\p{L}\p{N}]+/gu, ' ').trim()

// Wynik sprawdzenia: null albo { code, message } — komunikat mówi, co poprawić.
function checkMessage (text, words) {
    const value = String(text || '').trim()
    if (!value) return { code: 'empty', message: 'Wpisz wiadomość.' }
    if (value.length > MAX_LENGTH) return { code: 'length', message: `Wiadomość jest za długa — skróć ją do ${MAX_LENGTH} znaków (teraz: ${value.length}).` }
    const host = findForeignLink(value)
    if (host) return { code: 'link', message: `Usuń link do strony ${host}. W wiadomościach można wysyłać tylko linki do samochody.pl. Numer telefonu i adres e-mail możesz podać.`, host }
    const bad = findBadWord(value, words)
    if (bad) return { code: 'word', message: `Zmień słowo „${maskWord(bad)}” — tego słowa nie można wysłać.`, word: maskWord(bad) }
    return null
}

module.exports = { DEFAULT_WORDS, MAX_LENGTH, checkMessage, findBadWord, findForeignLink, findLink, maskWord, normalizeText, wordsOf }
